import {
  createAgentActivityApi,
  createRuntimeReportingApi,
} from "@wakeloop/api-client";
import { getAuthSessionState } from "../config/auth-session";
import { readDaemonServiceContract } from "../config/daemon-service-contract";
import { canAttachAuthTokenToRequest } from "../config/request-headers";
import { createCliApiClientFoundation } from "../space/api-client-foundation";
import { drainAgentActivityHookInbox, removeProjectInbox } from "./hook-inbox";
import {
  removeAgentActivityContextCache,
  removeAllAgentActivityContextCaches,
  writeAgentActivityContextCache,
} from "./local-context-cache";
import {
  AgentActivitySubmissionResultsMismatchError,
  applyAgentActivitySubmissionResults,
  deferAgentActivityOutbox,
  readAgentActivityOutboxHealth,
  readDueAgentActivityOutbox,
  removeProjectOutbox,
} from "./local-event-store";
import {
  scanLocalAgentActivity,
  startAgentActivityTranscriptWatchers,
} from "./local-observer";
import { withAgentActivityDeliveryLock } from "./local-state-lock";
import {
  listProjectBindings,
  removeAgentActivityBindingsWhileDeliveryLocked,
} from "./project-bindings";

export type AgentActivityLocalSyncResult = {
  health: Awaited<ReturnType<typeof readAgentActivityOutboxHealth>>;
  observedCount: number;
  reason: string | null;
  status: "idle" | "submitted" | "unavailable";
  submittedCount: number;
};

export async function syncLocalAgentActivity(input?: {
  reconcile?: boolean;
  refreshContext?: boolean;
  rootDir?: string;
}): Promise<AgentActivityLocalSyncResult> {
  const inbox = await drainAgentActivityHookInbox({ rootDir: input?.rootDir });
  const reconciliation =
    input?.reconcile === false
      ? { observedCount: 0 }
      : await scanLocalAgentActivity({
          drainInbox: false,
          rootDir: input?.rootDir,
        });
  const observedCount = inbox.observedCount + reconciliation.observedCount;
  const result = await withAgentActivityDeliveryLock(
    { rootDir: input?.rootDir },
    async () => await submitDueAgentActivity(input, observedCount)
  );
  if (input?.refreshContext !== false) {
    await refreshAgentActivityContextCaches({ rootDir: input?.rootDir }).catch(
      () => undefined
    );
  }
  return result;
}

async function submitDueAgentActivity(
  input: { rootDir?: string } | undefined,
  observedCount: number
): Promise<AgentActivityLocalSyncResult> {
  const dueRecords = await readDueAgentActivityOutbox({
    rootDir: input?.rootDir,
  });
  const bindings = await listProjectBindings({
    rootDir: input?.rootDir,
  });
  const boundProjects = new Set(
    bindings.map(
      (binding) => `${binding.organizationId}\u0000${binding.projectId}`
    )
  );
  const records = dueRecords.filter((record) =>
    boundProjects.has(
      `${record.event.organizationId}\u0000${record.event.projectId}`
    )
  );
  for (const record of dueRecords) {
    if (!records.includes(record)) {
      await removeProjectOutbox({
        organizationId: record.event.organizationId,
        projectId: record.event.projectId,
        rootDir: input?.rootDir,
      });
    }
  }
  if (records.length === 0) {
    return {
      health: await readAgentActivityOutboxHealth({ rootDir: input?.rootDir }),
      observedCount,
      reason: null,
      status: "idle",
      submittedCount: 0,
    };
  }
  const serviceContract = await readDaemonServiceContract().catch(() => null);
  if (
    !(
      serviceContract?.deviceId &&
      serviceContract.gatewayUrl &&
      serviceContract.lane
    )
  ) {
    await deferAgentActivityOutbox({ records, rootDir: input?.rootDir });
    return unavailable(input, observedCount, "service_contract_unavailable");
  }
  const auth = await getAuthSessionState().catch(() => null);
  if (
    !(
      auth?.state === "valid" &&
      auth.session &&
      canAttachAuthTokenToRequest(auth.session, serviceContract.gatewayUrl)
    )
  ) {
    await deferAgentActivityOutbox({ records, rootDir: input?.rootDir });
    return unavailable(input, observedCount, "auth_unavailable");
  }
  try {
    const foundation = createCliApiClientFoundation(serviceContract.gatewayUrl);
    const api = createRuntimeReportingApi(foundation);
    const registration = await api.submitRegistration({
      deviceId: serviceContract.deviceId,
      lane: serviceContract.lane,
    });
    if (
      !(
        registration.status !== "rejected" &&
        registration.wakeLoopServiceRuntimeId
      )
    ) {
      await deferAgentActivityOutbox({ records, rootDir: input?.rootDir });
      return unavailable(input, observedCount, "registration_rejected");
    }
    const response = await api.submitAgentActivityEvents({
      deviceId: serviceContract.deviceId,
      events: records.map((record) => record.event),
      lane: serviceContract.lane,
      wakeLoopServiceRuntimeId: registration.wakeLoopServiceRuntimeId,
    });
    await applyAgentActivitySubmissionResults({
      records,
      results: response.events,
      rootDir: input?.rootDir,
    });
    await disableRejectedAgentActivityBindings({
      records,
      results: response.events,
      rootDir: input?.rootDir,
    });
    return {
      health: await readAgentActivityOutboxHealth({ rootDir: input?.rootDir }),
      observedCount,
      reason: null,
      status: "submitted",
      submittedCount: response.events.filter(
        (event) => event.outcome === "accepted" || event.outcome === "reused"
      ).length,
    };
  } catch (error) {
    await deferAgentActivityOutbox({ records, rootDir: input?.rootDir });
    return unavailable(input, observedCount, classifySyncFailure(error));
  }
}

async function refreshAgentActivityContextCaches(input: {
  foundation?: ReturnType<typeof createCliApiClientFoundation>;
  rootDir?: string;
}) {
  const allBindings = await listProjectBindings({
    rootDir: input.rootDir,
  });
  if (allBindings.length === 0) {
    await removeAllAgentActivityContextCaches({ rootDir: input.rootDir });
    return;
  }
  let foundation = input.foundation;
  if (!foundation) {
    const serviceContract = await readDaemonServiceContract().catch(() => null);
    if (
      !(
        serviceContract?.gatewayUrl &&
        serviceContract.deviceId &&
        serviceContract.lane
      )
    ) {
      return;
    }
    const auth = await getAuthSessionState().catch(() => null);
    if (
      !(
        auth?.state === "valid" &&
        auth.session &&
        canAttachAuthTokenToRequest(auth.session, serviceContract.gatewayUrl)
      )
    ) {
      return;
    }
    foundation = createCliApiClientFoundation(serviceContract.gatewayUrl);
  }
  const snapshot = await createAgentActivityApi(foundation).readSnapshot();
  let bindings = await listProjectBindings({
    organizationId: snapshot.organizationId,
    rootDir: input.rootDir,
  });
  const activeProjectIds = new Set(
    snapshot.projects
      .filter((project) => project.archivedAtMs === null)
      .map((project) => project.id)
  );
  for (const binding of bindings) {
    if (!activeProjectIds.has(binding.projectId)) {
      await disableAndPurgeAgentActivityBindings({
        organizationId: binding.organizationId,
        projectId: binding.projectId,
        rootDir: input.rootDir,
      });
    }
  }
  bindings = await listProjectBindings({
    organizationId: snapshot.organizationId,
    rootDir: input.rootDir,
  });
  const projectBindings = selectOneBindingPerProjectScope(bindings);
  await Promise.all(
    projectBindings.map(async (binding) => {
      await writeAgentActivityContextCache({
        projectId: binding.projectId,
        rootDir: input.rootDir,
        snapshot,
      });
    })
  );
}

export function selectOneBindingPerProjectScope<
  T extends {
    isDefault: boolean;
    organizationId: string;
    projectId: string;
  },
>(bindings: readonly T[]): T[] {
  const projectBindings = new Map<string, T>();
  for (const binding of bindings) {
    const key = `${binding.organizationId}\0${binding.projectId}`;
    const current = projectBindings.get(key);
    if (!current || binding.isDefault) {
      projectBindings.set(key, binding);
    }
  }
  return [...projectBindings.values()];
}

async function disableAndPurgeAgentActivityBindings(input: {
  organizationId: string;
  projectId?: string;
  rootDir?: string;
}) {
  await withAgentActivityDeliveryLock(input, async () => {
    const removed = await removeAgentActivityBindingsWhileDeliveryLocked(input);
    await purgeDisabledAgentActivityBindings(removed, input.rootDir);
  });
}

async function disableRejectedAgentActivityBindings(input: {
  records: readonly Awaited<
    ReturnType<typeof readDueAgentActivityOutbox>
  >[number][];
  results: readonly {
    index: number;
    outcome: string;
    reason?: string;
  }[];
  rootDir?: string;
}) {
  const projectScopes = new Map<
    string,
    { organizationId: string; projectId?: string }
  >();
  for (const result of input.results) {
    if (result.outcome !== "permanent_rejected") {
      continue;
    }
    const record = input.records[result.index];
    if (!record) {
      continue;
    }
    if (result.reason === "membership_removed") {
      projectScopes.set(record.event.organizationId, {
        organizationId: record.event.organizationId,
      });
    } else if (
      result.reason === "project_archived" ||
      result.reason === "project_missing" ||
      result.reason === "repository_fingerprint_mismatch"
    ) {
      projectScopes.set(
        `${record.event.organizationId}\u0000${record.event.projectId}`,
        {
          organizationId: record.event.organizationId,
          projectId: record.event.projectId,
        }
      );
    }
  }
  for (const scope of projectScopes.values()) {
    const removed = await removeAgentActivityBindingsWhileDeliveryLocked({
      ...scope,
      rootDir: input.rootDir,
    });
    await purgeDisabledAgentActivityBindings(removed, input.rootDir);
  }
}

async function purgeDisabledAgentActivityBindings(
  bindings: readonly {
    organizationId: string;
    projectId: string;
  }[],
  rootDir?: string
) {
  const projectScopes = new Map(
    bindings.map((binding) => [
      `${binding.organizationId}\0${binding.projectId}`,
      binding,
    ])
  );
  for (const binding of projectScopes.values()) {
    await Promise.all([
      removeProjectInbox({
        organizationId: binding.organizationId,
        projectId: binding.projectId,
        rootDir,
      }),
      removeProjectOutbox({
        organizationId: binding.organizationId,
        projectId: binding.projectId,
        rootDir,
      }),
      removeAgentActivityContextCache({
        organizationId: binding.organizationId,
        projectId: binding.projectId,
        rootDir,
      }),
    ]);
  }
}

function classifySyncFailure(error: unknown): string {
  if (error instanceof AgentActivitySubmissionResultsMismatchError) {
    return "write_response_invalid";
  }
  if (error && typeof error === "object") {
    const status = "status" in error ? Number(error.status) : Number.NaN;
    if (status === 401 || status === 403) {
      return "write_auth_rejected";
    }
    if (status === 404) {
      return "write_endpoint_unavailable";
    }
    if (status === 408 || status === 429 || status >= 500) {
      return "write_retryable_http_failure";
    }
    if ("name" in error && String(error.name) === "AbortError") {
      return "write_timeout";
    }
  }
  return "write_transport_failure";
}

export function startLocalAgentActivitySyncLoop(input: {
  intervalMs?: number;
  onError?: (error: unknown) => void;
  onResult?: (result: AgentActivityLocalSyncResult) => void;
}): () => Promise<void> {
  let stopped = false;
  let inFlight: Promise<void> | null = null;
  let nextContextRefreshAtMs = 0;
  let nextReconciliationAtMs = 0;
  const stopWatchers = startAgentActivityTranscriptWatchers({
    onError: input.onError,
  });
  const run = () => {
    if (stopped || inFlight) {
      return;
    }
    const nowMs = Date.now();
    const refreshContext = nowMs >= nextContextRefreshAtMs;
    const reconcile = nowMs >= nextReconciliationAtMs;
    if (refreshContext) {
      nextContextRefreshAtMs = nowMs + 60_000;
    }
    if (reconcile) {
      nextReconciliationAtMs = nowMs + 15 * 60_000;
    }
    inFlight = syncLocalAgentActivity({ reconcile, refreshContext })
      .then((result) => input.onResult?.(result))
      .catch((error) => input.onError?.(error))
      .finally(() => {
        inFlight = null;
      });
  };
  const timer = setInterval(run, input.intervalMs ?? 5000);
  timer.unref();
  run();
  return async () => {
    stopped = true;
    clearInterval(timer);
    await inFlight;
    await stopWatchers();
  };
}

async function unavailable(
  input: { rootDir?: string } | undefined,
  observedCount: number,
  reason: string
): Promise<AgentActivityLocalSyncResult> {
  return {
    health: await readAgentActivityOutboxHealth({ rootDir: input?.rootDir }),
    observedCount,
    reason,
    status: "unavailable",
    submittedCount: 0,
  };
}
